import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { getTaxInvoiceSellers, saveTaxInvoiceSeller, deleteTaxInvoiceSeller } from '../utilities/api';
import '../masters/CategoryMaster.css';

const emptySeller = {
    setting_id: null,
    company_name: '',
    address: '',
    gst_no: '',
    phone: '',
    email: '',
    website: '',
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    bank_branch: '',
    status: 'Active'
};

const FieldLabel = ({ children, required }) => (
    <label style={{ fontWeight: '600', fontSize: '0.8rem', color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
        {children} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
);

const SectionDivider = ({ icon, title }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.4rem 0.8rem',
            borderRadius: '6px',
            background: 'linear-gradient(90deg, #f1f5f9, #f8fafc)',
            borderLeft: '3px solid #6d3fbf',
            margin: '1rem 0 0.75rem'
        }}
    >
        <i className={icon} style={{ color: '#6d3fbf', fontSize: '0.8rem' }} />
        <span style={{ fontWeight: '600', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
    </div>
);

const SellerMaster = () => {
    const toast = useRef(null);
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [form, setForm] = useState(emptySeller);
    const [submitted, setSubmitted] = useState(false);

    const reload = () => {
        setLoading(true);
        getTaxInvoiceSellers()
            .then((res) => setSellers(Array.isArray(res?.data) ? res.data : []))
            .catch((err) => toast.current?.show({ severity: 'error', summary: 'Load Error', detail: err?.message, life: 5000 }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
    }, []);

    const activeCount = useMemo(() => sellers.filter((s) => s.status === 'Active').length, [sellers]);

    const isInvalid = (name) => submitted && name === 'company_name' && !form.company_name?.trim();

    const handleSave = async () => {
        setSubmitted(true);
        if (!form.company_name?.trim()) return;
        try {
            const res = await saveTaxInvoiceSeller(form);
            if (res?.status) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.message || 'Saved', life: 3000 });
                setDisplayDialog(false);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: res?.message || 'Save failed', life: 4000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message, life: 4000 });
        }
    };

    const handleDelete = (row) => {
        confirmDialog({
            message: `Delete seller "${row.company_name}"? Invoices already raised under this seller keep their own snapshot and won't be affected.`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const res = await deleteTaxInvoiceSeller(row.setting_id);
                    toast.current.show({ severity: res?.status ? 'success' : 'error', summary: res?.status ? 'Deleted' : 'Error', detail: res?.message, life: 3000 });
                    reload();
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message, life: 4000 });
                }
            }
        });
    };

    const statusBody = (row) => <Tag value={row.status} severity={row.status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;
    const actionBody = (row) => (
        <div className="flex gap-1 justify-content-center">
            <Button
                icon="pi pi-pencil"
                rounded
                text
                className="p-button-warning"
                tooltip="Edit"
                onClick={() => {
                    setForm({ ...row });
                    setSubmitted(false);
                    setDisplayDialog(true);
                }}
            />
            <Button icon="pi pi-trash" rounded text className="p-button-danger" tooltip="Delete" onClick={() => handleDelete(row)} />
        </div>
    );

    return (
        <>
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Seller Master
                </h2>
                <p className="text-color-secondary">Manage the entities you invoice under — GST registration, bank details and branding for each</p>
            </div>

            <Card className="bill-reports-container">
                <Toast ref={toast} />
                <ConfirmDialog />

                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-semibold m-0">Sellers</h3>
                        <span className="tab-count" style={{ background: '#e2e8f0', color: '#475569', borderRadius: 10, padding: '0 0.5rem', fontSize: '0.8rem', fontWeight: 700 }}>
                            {activeCount} active
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={(e) => {
                                    setGlobalFilterValue(e.target.value);
                                    setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                                }}
                                placeholder="Search sellers..."
                                className="w-full"
                            />
                        </span>
                        <Button
                            label="Add Seller"
                            icon="pi pi-plus"
                            className="p-button-primary"
                            onClick={() => {
                                setForm({ ...emptySeller });
                                setSubmitted(false);
                                setDisplayDialog(true);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="sub-masters-table-wrap">
                        <DataTable
                            value={sellers}
                            filters={filters}
                            globalFilterFields={['company_name', 'gst_no', 'phone', 'email']}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                            showGridlines
                            tableStyle={{ minWidth: '50rem' }}
                            className="p-datatable-sm sub-masters-table"
                            emptyMessage="No sellers found"
                        >
                            <Column field="company_name" header="Company Name" sortable />
                            <Column field="gst_no" header="GST No" sortable style={{ width: '180px' }} />
                            <Column field="bank_name" header="Bank" sortable style={{ width: '160px' }} />
                            <Column field="status" header="Status" body={statusBody} sortable style={{ width: '120px', textAlign: 'center' }} />
                            <Column header="Actions" body={actionBody} style={{ width: '100px', textAlign: 'center' }} />
                        </DataTable>
                    </div>
                )}
            </Card>

            <Dialog
                visible={displayDialog}
                style={{ width: '620px' }}
                breakpoints={{ '768px': '92vw' }}
                header={form.setting_id ? 'Edit Seller' : 'Add Seller'}
                modal
                className="p-fluid"
                onHide={() => setDisplayDialog(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" outlined severity="secondary" onClick={() => setDisplayDialog(false)} />
                        <Button label={form.setting_id ? 'Update' : 'Save'} icon="pi pi-check" severity="success" onClick={handleSave} />
                    </div>
                }
            >
                <SectionDivider icon="pi pi-building" title="Company Details" />
                <div className="field" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Company Name</FieldLabel>
                    <InputText value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={classNames({ 'p-invalid': isInvalid('company_name') })} />
                </div>
                <div className="field" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Address</FieldLabel>
                    <InputTextarea value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
                </div>
                <div className="grid" style={{ margin: 0 }}>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>GST No</FieldLabel>
                        <InputText value={form.gst_no || ''} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} maxLength={15} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Website</FieldLabel>
                        <InputText value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Phone</FieldLabel>
                        <InputText value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Email</FieldLabel>
                        <InputText value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                </div>

                <SectionDivider icon="pi pi-wallet" title="Bank Details (shown on invoice)" />
                <div className="grid" style={{ margin: 0 }}>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Bank Name</FieldLabel>
                        <InputText value={form.bank_name || ''} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Account No</FieldLabel>
                        <InputText value={form.account_no || ''} onChange={(e) => setForm({ ...form, account_no: e.target.value })} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>IFSC Code</FieldLabel>
                        <InputText value={form.ifsc_code || ''} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })} />
                    </div>
                    <div className="field col-6" style={{ paddingBottom: '0.5rem' }}>
                        <FieldLabel>Branch</FieldLabel>
                        <InputText value={form.bank_branch || ''} onChange={(e) => setForm({ ...form, bank_branch: e.target.value })} />
                    </div>
                </div>

                <div className="field" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Status</FieldLabel>
                    <Dropdown
                        value={form.status}
                        options={[
                            { label: 'Active', value: 'Active' },
                            { label: 'Inactive', value: 'Inactive' }
                        ]}
                        onChange={(e) => setForm({ ...form, status: e.value })}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default SellerMaster;
